import {
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Texto secundário exibido à direita do rótulo (ex.: andar). */
  hint?: string | null;
}

interface SearchableSelectProps {
  id?: string;
  label: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Placeholder do campo quando nenhuma opção está selecionada. */
  placeholder?: string;
  /** Placeholder exibido quando o campo está desabilitado. */
  disabledPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  /** Ícone FontAwesome à esquerda (ex.: 'fa-building'). */
  icon?: string;
  /** Mensagem exibida quando a busca não retorna resultados. */
  emptyMessage?: string;
  autoFocus?: boolean;
}

/** Remove acentos e caixa para busca tolerante (ex.: "Ática" casa com "atica"). */
function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Realça o trecho que casa com a busca, para o usuário localizar rapidamente
 * a unidade certa entre centenas de opções.
 */
function Highlight({ text, query }: { text: string; query: string }) {
  const q = normalize(query);
  if (!q) return <>{text}</>;
  const idx = normalize(text).indexOf(q);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-sky-200 text-navy-900 rounded-[3px] px-[1px]">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

/**
 * Combobox acessível com busca por digitação. Segue o padrão WAI-ARIA de
 * combobox (input + listbox), com navegação por teclado, filtro tolerante a
 * acentos e realce do trecho encontrado. Substitui o `<select>` nativo em
 * listas grandes (condomínios com centenas de unidades).
 */
export function SearchableSelect({
  id,
  label,
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  disabledPlaceholder,
  disabled = false,
  required = false,
  error,
  hint,
  icon,
  emptyMessage = 'Nenhuma opção encontrada.',
  autoFocus = false,
}: SearchableSelectProps) {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const listId = `${fieldId}-listbox`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  // Enquanto aberto filtramos pela busca; fechado, o input exibe o rótulo atual.
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    return options.filter((o) => normalize(`${o.label} ${o.hint ?? ''}`).includes(q));
  }, [options, query]);

  const describedById = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
  const activeId = open && filtered[activeIndex] ? `${fieldId}-opt-${activeIndex}` : undefined;

  // Fecha ao clicar fora, revertendo a busca ao rótulo selecionado.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Mantém a opção ativa visível durante a navegação por teclado.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(`#${fieldId}-opt-${activeIndex}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, fieldId]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    // Começa destacando a opção já selecionada, se houver.
    const selIdx = filtered.findIndex((o) => o.value === value);
    setActiveIndex(selIdx >= 0 ? selIdx : 0);
  };

  const commit = (opt: SearchableSelectOption) => {
    onChange(opt.value);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) return openList();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) return openList();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (open && filtered[activeIndex]) {
          e.preventDefault();
          commit(filtered[activeIndex]);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
          setQuery('');
        }
        break;
      case 'Tab':
        setOpen(false);
        setQuery('');
        break;
    }
  };

  const displayValue = open ? query : selected?.label ?? '';

  return (
    <div className="mb-4" ref={rootRef}>
      <label htmlFor={fieldId} className="block mb-[6px] text-[12px] font-semibold text-navy-800">
        {label}
        {required && <span className="text-alert-caution ml-[2px]" aria-hidden="true">*</span>}
      </label>

      <div className="relative">
        <div
          className={`flex items-center bg-white rounded-[20px] h-[42px] border border-solid transition-colors ${
            disabled ? 'bg-mist-100' : ''
          } ${
            error
              ? 'border-alert-caution focus-within:ring-2 focus-within:ring-alert-caution/30'
              : 'border-mist-300 focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-500'
          }`}
        >
          {icon && (
            <i
              className={`fa ${icon} pl-[15px] text-[14px] ${disabled ? 'text-mist-300' : 'text-mist-500'}`}
              aria-hidden="true"
            />
          )}
          <input
            ref={inputRef}
            id={fieldId}
            type="text"
            role="combobox"
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={disabled}
            required={required}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-invalid={!!error}
            aria-describedby={describedById}
            placeholder={disabled ? disabledPlaceholder ?? placeholder : placeholder}
            value={displayValue}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
              if (!open) setOpen(true);
            }}
            onFocus={openList}
            onClick={openList}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-0 h-full px-[15px] border-0 rounded-[20px] text-[13px] text-navy-800 bg-transparent focus:outline-none placeholder:text-mist-500 disabled:text-mist-500"
          />

          {selected && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Limpar seleção"
              onClick={() => {
                onChange('');
                setQuery('');
                setOpen(false);
                inputRef.current?.focus();
              }}
              className="flex items-center justify-center w-[28px] h-full text-mist-500 hover:text-navy-700 bg-transparent border-0 cursor-pointer"
            >
              <i className="fa fa-times-circle text-[13px]" aria-hidden="true" />
            </button>
          )}
          <i
            className={`fa fa-chevron-down pr-[15px] text-[11px] text-mist-500 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </div>

        {open && (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label}
            className="absolute z-20 mt-[6px] w-full max-h-[240px] overflow-y-auto py-[5px] bg-white rounded-[14px] border border-solid border-mist-300 shadow-dropdown"
          >
            {filtered.length === 0 ? (
              <li className="px-[15px] py-[10px] text-[12px] text-mist-500" role="presentation">
                {emptyMessage}
              </li>
            ) : (
              filtered.map((opt, i) => {
                const isActive = i === activeIndex;
                const isSelected = opt.value === value;
                return (
                  <li
                    key={opt.value}
                    id={`${fieldId}-opt-${i}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      // Evita o blur do input antes do clique registrar.
                      e.preventDefault();
                      commit(opt);
                    }}
                    className={`flex items-center justify-between gap-3 px-[15px] py-[8px] text-[13px] cursor-pointer ${
                      isActive ? 'bg-sky-50' : ''
                    } ${isSelected ? 'text-navy-900 font-semibold' : 'text-navy-800'}`}
                  >
                    <span className="truncate">
                      <Highlight text={opt.label} query={query} />
                    </span>
                    {opt.hint && (
                      <span className="shrink-0 text-[11px] text-navy-700/70">{opt.hint}</span>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {error ? (
        <p id={`${fieldId}-error`} className="mt-[5px] text-[11px] text-alert-caution flex items-center gap-1">
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-[5px] text-[11px] text-navy-700/70">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
